import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { getCustomerDetail } from "../services/customers.service";
import { setCurrentCustomer } from "../services/user.service";
import { getUserDetailsByToken } from "../services/readUsers.service";
import { useAuth } from "./AuthContext";

type CustomerSummary = {
  customer_id: number;
  customer_name: string;
  tenant_id?: string;
  customer_name_short?: string;
  environment_count?: number;
};

interface CustomerContextValue {
  customers: CustomerSummary[];
  activeCustomerId?: number;
  activeCustomer?: CustomerSummary;
  isLoading: boolean;
  isSwitching: boolean;
  hasMultipleCustomers: boolean;
  selectCustomer: (customerId: number) => Promise<void>;
  refreshCustomers: () => Promise<void>;
  userDetails?: any;
}

const CustomerContext = createContext<CustomerContextValue>({
  customers: [],
  activeCustomerId: undefined,
  activeCustomer: undefined,
  isLoading: false,
  isSwitching: false,
  hasMultipleCustomers: false,
  selectCustomer: async () => undefined,
  refreshCustomers: async () => undefined,
  userDetails: undefined,
});

const syncCustomerLocalStorage = (
  activeCustomerId?: number,
  customerIds?: number[]
) => {
  if (typeof activeCustomerId === "number") {
    localStorage.setItem("customer_id", String(activeCustomerId));
  } else {
    localStorage.removeItem("customer_id");
  }

  if (customerIds && customerIds.length > 0) {
    localStorage.setItem("customer_ids", JSON.stringify(customerIds));
  } else {
    localStorage.removeItem("customer_ids");
  }
};

const fetchCustomerSummaries = async (customerIds: number[]) => {
  if (!customerIds.length) {
    return [];
  }

  const results = await Promise.all(
    customerIds.map(async (customerId) => {
      try {
        const detail = await getCustomerDetail(customerId);
        return {
          customer_id: detail.customer_id,
          customer_name: detail.customer_name,
          customer_name_short: detail.customer_name_short,
          tenant_id: (detail as any).tenant_id,
          environment_count: Array.isArray(detail.environment)
            ? detail.environment.length
            : undefined,
        } as CustomerSummary;
      } catch (error) {
        console.error(
          `Failed to fetch customer ${customerId} details`,
          error
        );
        return {
          customer_id: customerId,
          customer_name: `Customer ${customerId}`,
        } as CustomerSummary;
      }
    })
  );

  return results.filter(Boolean);
};

export const CustomerProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { tokens } = useAuth();
  const [customers, setCustomers] = useState<CustomerSummary[]>([]);
  const [customerIds, setCustomerIds] = useState<number[]>([]);
  const [activeCustomerId, setActiveCustomerId] = useState<number | undefined>(
    undefined
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const [userDetails, setUserDetails] = useState<any | undefined>(undefined);

  const hydrate = useCallback(async () => {
    if (!tokens.accessToken) {
      setCustomers([]);
      setCustomerIds([]);
      setActiveCustomerId(undefined);
      setUserDetails(null);
      syncCustomerLocalStorage(undefined, []);
      return;
    }

    setIsLoading(true);
    try {
      const user = await getUserDetailsByToken();
      const ids: number[] = Array.isArray(user?.customer_ids)
        ? user.customer_ids
        : user?.customer_id
          ? [user.customer_id]
          : [];

      setCustomerIds(ids);

      let currentId: number | undefined =
        typeof user?.current_customer_id === "number"
          ? user.current_customer_id
          : undefined;

      if (!currentId && ids.length > 0) {
        currentId = ids[0];
        try {
          await setCurrentCustomer(currentId);
        } catch (err) {
          console.error("Failed to persist default customer selection", err);
        }
      }

      setActiveCustomerId(currentId);
      syncCustomerLocalStorage(currentId, ids);

      const summaries = await fetchCustomerSummaries(ids);
      setCustomers(summaries);
      setUserDetails(user);
    } catch (error) {
      console.error("Failed to hydrate customer context", error);
      setCustomers([]);
      setCustomerIds([]);
      setActiveCustomerId(undefined);
      setUserDetails(null);
      syncCustomerLocalStorage(undefined, []);
    } finally {
      setIsLoading(false);
    }
  }, [tokens.accessToken]);

  useEffect(() => {
    hydrate();
  }, [hydrate]);

  const selectCustomer = useCallback(
    async (customerId: number) => {
      if (customerId === activeCustomerId) {
        return;
      }

      setIsSwitching(true);
      try {
        await setCurrentCustomer(customerId);
        setActiveCustomerId(customerId);
        syncCustomerLocalStorage(customerId, customerIds);
        await hydrate();
      } catch (error) {
        console.error("Failed to switch customer", error);
        throw error;
      } finally {
        setIsSwitching(false);
      }
    },
    [activeCustomerId, customerIds, hydrate]
  );

  const refreshCustomers = useCallback(async () => {
    await hydrate();
  }, [hydrate]);

  const value: CustomerContextValue = useMemo(() => {
    const activeCustomer = customers.find(
      (customer) => customer.customer_id === activeCustomerId
    );

    return {
      customers,
      activeCustomerId,
      activeCustomer,
      isLoading,
      isSwitching,
      hasMultipleCustomers: customerIds.length > 1,
      selectCustomer,
      refreshCustomers,
      userDetails,
    };
  }, [
    customers,
    activeCustomerId,
    isLoading,
    isSwitching,
    customerIds.length,
    selectCustomer,
    refreshCustomers,
    userDetails,
  ]);

  return (
    <CustomerContext.Provider value={value}>
      {children}
    </CustomerContext.Provider>
  );
};

export const useCustomer = () => useContext(CustomerContext);
